import { Component, Inject, Injector, OnInit } from "@angular/core";
import { Meta } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { PublicChargeSubscriptionDto as PublicTransactionRequestDto } from "@dto/publicChargeSubscriptionDto";
import { ChargingStationDto } from "@dto/chargingStationDto";
import { PricingDto } from "@dto/pricingDto";
import { ChargingStationsService } from "@services/entity/charging-stations.service";
import { PricingsService } from "@services/entity/pricings.service";
import { PaymentsService } from "@services/payments/payments.service";
import { latLng, marker, tileLayer, Map, Layer, divIcon } from "leaflet";
import { UserDto } from "@dto/userDto";
import { FormService } from "@services/form/form.service";
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { ToastService } from "@services/toast/toast.service";
import { UsersService } from "@services/entity/users.service";
import { StripeInfosService } from "@services/entity/stripe-infos.service";
import { loadStripe } from "@stripe/stripe-js";
import { ComponentBase } from "@abstracts/component-base";
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from "@angular/animations";
import { DatasecurityComponent } from "@components/authentication/datasecurity/datasecurity.component";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Observable } from "rxjs";
import { ChargingPoint } from "@models/chargingPoint";
import { ChargingPointStatusEnum } from "@enums/chargingPointStatus.enum";

@Component({
  selector: "app-public-charge-plans-en",
  templateUrl: "./public-charge-plans-en.component.html",
  styleUrls: ["./public-charge-plans-en.component.scss"],
  animations: [
    trigger("drawerState", [
      state(
        "closed",
        style({
          height: "0px",
          overflow: "hidden",
          opacity: 0,
        })
      ),
      state(
        "open",
        style({
          height: "*",
          opacity: 1,
        })
      ),
      transition("closed <=> open", [animate("300ms ease-in-out")]),
    ]),
  ],
})
export class PublicChargePlansEnComponent
  extends ComponentBase
  implements OnInit
{
  public className = "PublicChargePlansEnComponent";

  public chargingStation?: ChargingStationDto;
  public email: string;
  public statusInterval: any;
  public isInitiated: boolean = false;
  public isAnyChargingPointAvailable: boolean = false;
  public whitelabel: string;
  public drawerOpen: boolean = false;

  private map: Map;
  public mapOptions = {
    zoom: 18,
    boxZoom: false,
    zoomControl: false,
    doubleClickZoom: false,
    dragging: false,
    layers: [
      tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        minZoom: 18,
        maxZoom: 18,
        attribution: "© OpenStreetMap",
      }),
    ],
  };
  public markers: Layer[] = [];

  public submitButtonCaption: string = "Weiter";
  public isShowingForm: boolean = false;

  public currentUser: UserDto;
  public parkOwner: UserDto;
  public parkOwnerStripeAccountId: string;
  public pricing?: PricingDto;
  public formCreated: boolean = false;
  public subscriptionFormService: FormService;
  public userId: string;

  public precisionCharge: boolean = false;
  public chargingUnit: string = "euro";
  public chargingAmount: number = 3000;

  public isCreatingStripeForm: boolean = false;
  public stripe: any;
  public stripeElements: any;
  public stripeCard: any;
  public publicTransactionRequest: PublicTransactionRequestDto;
  public isPaying: boolean = false;
  public loginUrl: string = "";
  public hasStoredAccessToken: boolean = false;
  public isAuthenticated: boolean = false;

  constructor(
    @Inject("BASE_URL") private baseUrl: string,
    private paymentsService: PaymentsService,
    private stripeInfosService: StripeInfosService,
    private chargingStationsService: ChargingStationsService,
    private pricingsService: PricingsService,
    private route: ActivatedRoute,
    private router: Router,
    public toastService: ToastService,
    private usersService: UsersService,
    private dialog: NgbModal,
    private metaTagService: Meta,
    injector: Injector
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.whitelabel = localStorage.getItem("whiteLabel");

    super.ngOnInit();
    this.setupStripe();
    this.updateMetaTags();
  }

  afterInit() {
    this.updateMap();
  }

  async setupStripe() {
    this.paymentsService.getStripePublishableKey().subscribe(
      async (stripe) => {
        this.stripe = await loadStripe(stripe.publishableKey, { locale: "en" });
        this.getChargingStation();
      },
      (error) => {
        console.error(error);
      }
    );
  }

  updateMetaTags() {
    this.metaTagService.updateTag({
      property: "og:title",
      content: "QR-Code-Laden Powered by CUBOS",
    });
    this.metaTagService.updateTag({
      property: "og:type",
      content: "website",
    });
    this.metaTagService.updateTag({
      property: "og:description",
      content:
        "QR-Code-Laden ist einfach: Erst Ladekabel verbinden, dann Zahlungsdaten angeben und losladen.",
    });
    this.metaTagService.updateTag({
      property: "og:image",
      content: window.location.origin + "/assets/images/cubos-og-image.jpg",
    });
    this.metaTagService.updateTag({
      property: "og:url",
      content: window.location.href,
    });
  }

  createStripeForm() {
    const elements = this.stripe.elements({
      clientSecret: this.publicTransactionRequest.stripeClientSecret,
      appearance: {
        theme: "flat",
        rules: {
          ".Label": {
            marginBottom: "0.5rem",
            fontSize: "0.875rem",
            color: "#212529",
            lineHeight: "1.5",
          },
          ".Input": {
            border: "1px solid #ced4da",
            boxShadow: "inset 0 1px 2px rgb(0, 0, 0, .08)",
            padding: "0.375rem 0.75rem",
            fontSize: "0.875rem",
            color: "#212529",
            lineHeight: "1.5",
          },
        },
        variables: {
          colorPrimary: "#ef7c00",
          colorBackground: "#ffffff",
          colorText: "#000",
          colorDanger: "#df1b41",
          fontFamily: "Lato, system-ui, sans-serif",
          spacingUnit: "3px",
          borderRadius: "4px",
        },
      },
    });

    const paymentElement = elements.create("payment");
    paymentElement.mount("#payment-element");

    const form = document.getElementById("payment-form");
    form.addEventListener("submit", async (event) => {
      event.preventDefault();

      const updateAmount = this.precisionCharge;
      await this.handlePaymentSubmission(updateAmount, elements);
    });
  }

  async handlePaymentSubmission(updateAmount: boolean, elements: any) {
    try {
      let payAmount: number = 0;
      if (updateAmount) {
        // kWh umrechnen in €
        if (this.chargingUnit == "kwh") {
          payAmount = this.chargingAmount * this.pricing.price;
        } else {
          payAmount = this.chargingAmount;
        }

        // €-Betrag notfalls aufrunden
        if (payAmount < 100) {
          payAmount = 100;
        }
        const result = await this.paymentsService
          .updatePrecisionChargeAmount(
            this.publicTransactionRequest.stripePaymentIntentId,
            payAmount
          )
          .toPromise();

        if (result.amount !== payAmount) {
          throw new Error("Amount mismatch");
        }
      }

      this.isPaying = true;

      const url = `${this.baseUrl}public-charge?charging-station=${this.publicTransactionRequest.chargingStationId}&payment-intent=${this.publicTransactionRequest.stripePaymentIntentId}`;
      const { error } = await this.stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: url,
        },
      });

      if (error) {
        const messageContainer = document.querySelector("#error-message");
        messageContainer.textContent = error.message;
      } else {
        // Your customer will be redirected to your `return_url`. For some payment
        // methods like iDEAL, your customer will be redirected to an intermediate
        // site first to authorize the payment, then redirected to the `return_url`.
      }
    } catch (error) {
      console.error("Error during payment submission:", error);
      const messageContainer = document.querySelector("#error-message");
      messageContainer.textContent = error.message;
    }
  }

  createToken() {
    this.stripe.createToken(this.stripeCard).then((result) => {
      if (result.error) {
        var errorElement = document.getElementById("card-errors");
        errorElement.textContent = result.error.message;
      } else {
        this.stripe.stripeTokenHandler(result.token).then((token) => {
          this.stripe.redirectToCheckout({
            sessionId: token,
          });
        });
      }
    });
  }

  getChargingStation() {
    // example
    // https://mycubos.com/pc?cs=52a282e8-ddc1-4cf6-0e02-08dcddf06be9
    const chargingStationId = this.route.snapshot.queryParams["cs"];

    // example
    // https://mycubos.com/pc?evseid=DECUBS00000002
    const chargingPointEvseid = this.route.snapshot.queryParams["evseid"];

    var csDetailsQuery: Observable<ChargingStationDto>;
    if (chargingPointEvseid) {
      csDetailsQuery =
        this.chargingStationsService.getChargingStationPublicDetailsByEvseId(
          chargingPointEvseid
        );
    } else {
      csDetailsQuery =
        this.chargingStationsService.getChargingStationPublicDetails(
          chargingStationId
        );
    }

    csDetailsQuery.subscribe(
      (chargingStation) => {
        this.chargingStation = chargingStation;
        this.loginUrl = this.buildLoginUrl();
        this.updateAuthFlags();

        if (this.isAuthenticated && this.chargingStation?.id) {
          this.router.navigate([
            "/chargingstations/summary/edit",
            this.chargingStation.id,
          ]);
          return;
        }

        if (this.chargingStation != null) {
          this.updateChargingPointAvailability();
        }

        if (this.chargingStation == null) {
          this.isInitiated = true;
        } else if (this.chargingStation.isPublicCharge == false) {
          this.isInitiated = true;
        } else {
          this.statusInterval = window.setInterval(() => {
            this.updateStatus();
          }, 30000);

          this.createForm();
          this.getPricing();
        }
      },
      (error) => {
        console.error(error);
      }
    );
  }

  private buildLoginUrl(): string {
    if (!this.chargingStation?.id) {
      return "";
    }
    const returnUrl = `/chargingstations/summary/edit/${this.chargingStation.id}`;
    return `${window.location.origin}/auth/login?returnUrl=${encodeURIComponent(
      returnUrl
    )}`;
  }

  private updateAuthFlags(): void {
    this.hasStoredAccessToken = !!localStorage.getItem("accessToken");
    this.isAuthenticated = this.authenticationService.isUserAuthenticated();
  }

  getPricing() {
    if (this.chargingStation.customerId) {
      this.pricingsService
        .getPublicPricingOfCustomer(this.chargingStation.customerId)
        .subscribe((pricing) => {
          this.pricing = pricing;
          this.isInitiated = true;

          this.usersService
            .getUserNameById(pricing.ownerUserId)
            .subscribe((owner) => {
              this.parkOwner = owner;

              this.loadStripeInfo();
            });
        });
    } else {
      console.warn(
        `Charging Station ${this.chargingStation.id} is missing a customer ID.`
      );
    }
  }

  loadStripeInfo() {
    this.stripeInfosService
      .getStripeInfoOfUser(this.pricing.ownerUserId)
      .subscribe(
        (result) => {
          this.parkOwnerStripeAccountId = result.stripeAccountId;
        },
        (error) => {
          console.error(error);
        }
      );
  }

  onMapReady(map: Map) {
    this.map = map;
    this.map.invalidateSize();
    this.updateMap();
    window.dispatchEvent(new Event("resize"));
  }

  updateMap() {
    const lat = this.chargingStation.lat;
    const lon = this.chargingStation.lon;
    if (this.map && lat && lon) {
      this.map.setView(latLng(parseFloat(lat), parseFloat(lon)));
      this.map.attributionControl.setPosition("topright");
      const newMarker = marker([parseFloat(lat), parseFloat(lon)], {
        icon: divIcon({
          html: `
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-map-pin" id="map-marker">
                <path fill="#EF7C00" stroke="#EF7C00" d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle fill="#FFCB00" stroke="#FFCB00" cx="12" cy="10" r="3"></circle>
              </svg>
            `,
          className: "",
          iconSize: [48, 48],
          iconAnchor: [12, 24],
        }),
      });
      this.map.addLayer(newMarker);
      this.map.invalidateSize();
      window.dispatchEvent(new Event("resize"));
    } else {
      console.warn(`Charging station is undefined or has no location.`);
    }
  }

  updateStatus() {
    this.chargingStationsService
      .getChargingStationPublicDetails(this.chargingStation.id)
      .subscribe((chargingStation) => {
        this.chargingStation = chargingStation;
        this.updateChargingPointAvailability();
        this.updateAuthFlags();
      });
  }

  parsePrice(price) {
    return price.toLocaleString("de-DE", {
      style: "currency",
      currency: "EUR",
    });
  }

  parseWattHour(wattHour) {
    return Math.abs(wattHour / 100).toFixed(0);
  }

  scrollTo(anchor) {
    const x = document.getElementById(anchor).offsetTop;
    if (x) window.scrollTo(0, x);
    this.drawerOpen = true;
  }

  createForm() {
    this.subscriptionFormService = new FormService();

    this.subscriptionFormService.validators = {
      email: [Validators.required, Validators.email],
      company: [],
      name: [],
      street: [],
      housenumber: [],
      postcode: [],
      city: [],
      agbConfirm: [Validators.requiredTrue],
      dsbConfirm: [Validators.requiredTrue],
      bookingConfirm: [Validators.requiredTrue],
      chargingPointId: [Validators.required],
    };

    this.subscriptionFormService.form = new UntypedFormGroup({
      anonymous: new UntypedFormControl(true, []),
      agbConfirm: new UntypedFormControl(
        false,
        this.subscriptionFormService.validators["agbConfirm"]
      ),
      dsbConfirm: new UntypedFormControl(
        false,
        this.subscriptionFormService.validators["dsbConfirm"]
      ),
      bookingConfirm: new UntypedFormControl(
        false,
        this.subscriptionFormService.validators["bookingConfirm"]
      ),
      email: new UntypedFormControl(
        "",
        this.subscriptionFormService.validators["email"]
      ),
      method: new UntypedFormControl("card", []),
      company: new UntypedFormControl("", []),
      name: new UntypedFormControl(
        "",
        this.subscriptionFormService.validators["name"]
      ),
      street: new UntypedFormControl(
        "",
        this.subscriptionFormService.validators["street"]
      ),
      housenumber: new UntypedFormControl(
        "",
        this.subscriptionFormService.validators["housenumber"]
      ),
      postcode: new UntypedFormControl(
        "",
        this.subscriptionFormService.validators["postcode"]
      ),
      city: new UntypedFormControl(
        "",
        this.subscriptionFormService.validators["city"]
      ),
      /**
       * The actual charging point database index, not the connector id.
       */
      chargingPointId: new UntypedFormControl(
        // pre-select charging point if charging station only has one point.
        this.chargingStation.chargingPoints.length == 1
          ? this.chargingStation.chargingPoints[0].id
          : "",
        this.subscriptionFormService.validators["chargingPointId"]
      ),
    });

    this.formCreated = true;

    window.setTimeout(() => {
      const elementToFocus = document.getElementById("subscription-email");
      if (elementToFocus) elementToFocus.focus();
    }, 1000);

    this.subscriptionFormService.form
      .get("anonymous")
      .valueChanges.subscribe((value) => {
        this.updateFormValidator(value);
      });
  }

  onSubmit(formValue) {
    this.submitButtonCaption = `Buchen und laden`;

    let publicTransactionRequest = new PublicTransactionRequestDto();

    publicTransactionRequest.chargingStationId = this.chargingStation.id;
    publicTransactionRequest.chargingPointId = formValue.chargingPointId; // the actual charging point database index (not connectorid)
    publicTransactionRequest.parkOwnerName = this.parkOwner.company
      ? this.parkOwner.company
      : `${this.parkOwner.firstName} ${this.parkOwner.lastName}`;
    publicTransactionRequest.parkOwnerStripeAccountId =
      this.parkOwnerStripeAccountId;
    publicTransactionRequest.stripePriceId = this.pricing.priceId;

    publicTransactionRequest.email = formValue.email;
    publicTransactionRequest.method = formValue.method;

    if (!this.anonymous) {
      // publicTransactionRequest.company = formValue.company
      publicTransactionRequest.name = formValue.name;
      publicTransactionRequest.street = formValue.street;
      publicTransactionRequest.housenumber = formValue.housenumber;
      publicTransactionRequest.postcode = formValue.postcode;
      publicTransactionRequest.city = formValue.city;
    }

    publicTransactionRequest.country = "DE";

    localStorage.setItem("email", formValue.email);

    this.isCreatingStripeForm = true;
    this.paymentsService
      .createPublicTransactionPaymentIntent(publicTransactionRequest)
      .subscribe(
        (publicTransactionRequest) => {
          this.publicTransactionRequest = publicTransactionRequest;
          this.createStripeForm();
        },
        (error) => console.error(error)
      );
  }

  toggleDrawer() {
    this.drawerOpen = !this.drawerOpen;
  }

  get anonymous() {
    return this.subscriptionFormService.form.get("anonymous").value;
  }

  get chargingAmountPreview() {
    if (!this.precisionCharge) return 60;
    let chargingAmountPreview = 60;

    if (this.chargingUnit == "kwh") {
      chargingAmountPreview = (this.chargingAmount * this.pricing.price) / 100;
    } else {
      chargingAmountPreview = this.chargingAmount / 100;
    }

    if (chargingAmountPreview <= 1) {
      chargingAmountPreview = 1;
    }
    return chargingAmountPreview;
  }

  updateFormValidator(anonymous: boolean) {
    const nameControl = this.subscriptionFormService.form.get("name");
    const streetControl = this.subscriptionFormService.form.get("street");
    const housenumberControl =
      this.subscriptionFormService.form.get("housenumber");
    const postcodeControl = this.subscriptionFormService.form.get("postcode");
    const cityControl = this.subscriptionFormService.form.get("city");

    if (anonymous) {
      nameControl.setValidators([]);
      streetControl.setValidators([]);
      housenumberControl.setValidators([]);
      postcodeControl.setValidators([]);
      cityControl.setValidators([]);
    } else {
      nameControl.setValidators([Validators.required]);
      streetControl.setValidators([Validators.required]);
      housenumberControl.setValidators([Validators.required]);
      postcodeControl.setValidators([Validators.required]);
      cityControl.setValidators([Validators.required]);
    }
    nameControl.updateValueAndValidity();
    streetControl.updateValueAndValidity();
    housenumberControl.updateValueAndValidity();
    postcodeControl.updateValueAndValidity();
    cityControl.updateValueAndValidity();
  }

  openDSDialog() {
    const dialogRef = this.dialog.open(DatasecurityComponent, {
      centered: true,
      backdrop: true,
    });
    dialogRef.result.then(
      (result) => {
        //this.ngOnInit();
      },
      (backdrop) => {}
    );
  }

  getFullYear() {
    return new Date().getFullYear();
  }

  togglePrecisionCharge() {
    this.precisionCharge = !this.precisionCharge;
  }

  toggleUnit() {
    this.chargingUnit == "euro"
      ? (this.chargingUnit = "kwh")
      : (this.chargingUnit = "euro");
  }

  selectAmount(value: number) {
    this.chargingAmount == value ? null : (this.chargingAmount = value);
  }

  //if there is any ChargingPoints that is "Bereit" or "Vorbereitung", set isAn
  updateChargingPointAvailability() {
    for (var chargingPoint of this.chargingStation.chargingPoints) {
      if (this.isChargingPointAvailable(chargingPoint)) {
        this.isAnyChargingPointAvailable = true;
        return;
      }
    }
    this.isAnyChargingPointAvailable = false;
    return;
  }

  isChargingPointAvailable(point: ChargingPoint) {
    // unavailable, if cs is not available
    if (this.chargingStation.statusId != ChargingPointStatusEnum.Available) {
      return false;
    }

    // unavailable, if point is not available (or preparing / plugged in)
    if (
      point.statusId != ChargingPointStatusEnum.Available &&
      point.statusId != ChargingPointStatusEnum.Preparing
    ) {
      return false;
    }

    // available, else
    return true;
  }

  pageBack() {
    this.publicTransactionRequest = null;
    this.isCreatingStripeForm = null;
  }

  switchToGerman() {
    // Preserve query parameters when switching to German version
    const queryParams = this.route.snapshot.queryParams;
    this.router.navigate(["/public-charge-plans"], { queryParams });
  }
}
